import { clsx } from 'clsx';
import { useMobile } from 'common-nextjs';
import { useSelect } from 'downshift';
import React, { useEffect, useMemo, useState } from 'react';
import { Input } from 'ui';
import { getDetailById, getDetailPathById } from 'utils';
import { SelectorIcon } from '~/components/Icons/SelectorIcon';
import SearchIcon from '~/public/static/svg/SearchIcon';
import { RailsCategory } from '~/typings/services/rails/item';
import flattenCategories from '~/utils/flattenCategories';

interface Props {
  categories?: RailsCategory[];
  clearable?: boolean;
  id: string;
  onChange: (category: RailsCategory | null) => void;
  value: number | RailsCategory | null;
}

const CategorySelector: React.FC<Props> = ({
  categories,
  clearable,
  id,
  onChange,
  value,
}) => {
  const mobile = useMobile();
  const maxHeight = 400;
  const inputId = `${id}-search`;

  const selectedItem = useMemo(() => {
    let category: RailsCategory | null = null;
    if (value) {
      if (typeof value === 'number') {
        category = getDetailById(categories, value) || null;
      } else if (value.name && value.id) {
        category = value;
      }
    }

    return category;
  }, [categories, value]);

  const [inputValue, setInputValue] = useState('');
  const [submenuCategory, setSubmenuCategory] = useState<
    null | RailsCategory[]
  >(null);

  const lastSubmenuCategory = submenuCategory
    ? submenuCategory[submenuCategory.length - 1]
    : null;

  const flattenedCategories = useMemo(() => {
    if (categories) {
      return flattenCategories(categories).map(c => ({
        ...c,
        filterableName: c.full_name.replace(' > ', ' '),
      }));
    } else {
      return [];
    }
  }, [categories]);

  const filteredItems = useMemo(() => {
    const re = new RegExp(inputValue, 'i');
    return flattenedCategories.filter(category =>
      re.test(category.filterableName),
    );
  }, [flattenedCategories, inputValue]);

  let items: RailsCategory[];

  if (inputValue) {
    items = filteredItems;
  } else if (lastSubmenuCategory?.children) {
    items = lastSubmenuCategory?.children;
  } else {
    items = categories || [];
  }

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    closeMenu,
    setHighlightedIndex,
    reset,
  } = useSelect({
    id,
    items,
    selectedItem,
    itemToString: item => item?.full_name || '',
    stateReducer: (state, changes) => {
      if (
        changes.type === useSelect.stateChangeTypes.MenuBlur &&
        document.activeElement?.id === inputId
      ) {
        return state;
      }

      if (
        changes.type === useSelect.stateChangeTypes.ItemClick ||
        changes.type === useSelect.stateChangeTypes.MenuKeyDownEnter
      ) {
        const category = changes.changes.selectedItem || null;
        if (!category) {
          onChange(category);
          return changes.changes;
        }

        if (category.relatable) {
          onChange(category);
          return changes.changes;
        } else {
          setSubmenuCategory(s => (s ? s.concat(category) : [category]));
          return { ...state, highlightedIndex: -1 };
        }
      }

      // Ignore space button
      if (
        changes.type === useSelect.stateChangeTypes.MenuKeyDownSpaceButton ||
        changes.key === 'Space'
      ) {
        handleInputChange(inputValue + ' ');
        return state;
      }
      return changes.changes;
    },
  });

  useEffect(() => {
    if (isOpen && !mobile) {
      document.getElementById(inputId)?.focus();
    }
  }, [inputId, isOpen, mobile]);

  // Updates the submenuCategory array if `value` changes
  useEffect(() => {
    if (value && categories) {
      const path = getDetailPathById(
        categories,
        typeof value === 'number' ? value : value.id,
      );

      if (path && path.length > 0) {
        setSubmenuCategory(path.slice(0, -1));
      }
    }
  }, [value, categories]);

  function handleChange(category: RailsCategory | null) {
    onChange(category);
  }

  function handleReset() {
    reset();
    setInputValue('');
    setSubmenuCategory(null);
    handleChange(null);
  }

  function handleInputChange(value: string) {
    setInputValue(value);
    setSubmenuCategory(null);
    setHighlightedIndex(0);
  }

  return (
    <div className="relative">
      <div
        {...getToggleButtonProps()}
        className={clsx(
          'flex w-full cursor-pointer items-center focus-within:border-green-500 focus-within:ring',
          isOpen
            ? 'rounded-t-md border-l border-r border-t'
            : 'rounded-md border',
        )}
      >
        <button
          className={clsx(
            'flex-1 rounded px-4 py-2 text-left outline-none',
            selectedItem ? 'font-semibold' : '',
          )}
          type="button"
        >
          {selectedItem?.full_name}
        </button>

        {(!!inputValue || !!selectedItem) && clearable && (
          <button
            type="button"
            className="p-2"
            onClick={() => {
              handleChange(null);
              handleReset();
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
        <button type="button" tabIndex={-1} className="py-2.5 pl-1 pr-2">
          <SelectorIcon />
        </button>
      </div>

      <ul
        {...getMenuProps()}
        className="absolute top-full z-20 w-full rounded-b-md border-b border-l border-r bg-white shadow-lg"
        style={{
          display: isOpen ? 'block' : 'none',
        }}
      >
        {isOpen && (
          <>
            <Input
              leftAdornment={
                <SearchIcon
                  className="text-slate-green-500 ml-2"
                  fill="currentColor"
                />
              }
              id={inputId}
              onFocus={e => e.target.select()}
              type="search"
              inputClassName="!py-4 rounded-sm"
              className="mx-4 mb-4 mt-2 rounded-sm"
              placeholder="Search category..."
              onChange={e => handleInputChange(e.target.value)}
              value={inputValue}
            />

            {!inputValue && (
              <div
                className="mt-2 overflow-y-auto"
                style={{
                  maxHeight,
                }}
              >
                {!!submenuCategory && (
                  <div className="bg-off-white-500 mx-4 mb-4 flex flex-wrap items-center space-x-2 rounded-sm border px-2 py-1 text-sm">
                    <button
                      type="button"
                      onClick={() => setSubmenuCategory(null)}
                    >
                      All Categories
                    </button>

                    {!!submenuCategory && <span>&gt;</span>}
                    {submenuCategory?.map((category, i, array) => (
                      <React.Fragment key={category.id}>
                        <button
                          type="button"
                          onClick={() =>
                            setSubmenuCategory(array.slice(0, i + 1))
                          }
                          className="font-semibold"
                        >
                          {category.name}
                        </button>

                        {i !== array.length - 1 && <span>&gt;</span>}
                      </React.Fragment>
                    ))}
                  </div>
                )}

                <ul>
                  {items?.map((category, i) => (
                    <li
                      key={category.id}
                      {...getItemProps({
                        item: category,
                        index: i,
                      })}
                      className={clsx(
                        'hover:bg-pastel-green-500 flex w-full items-center space-x-1 px-4 py-2 text-left md:py-1',
                        highlightedIndex === i && 'bg-pastel-green-500',
                      )}
                    >
                      <span>{category.name}</span>
                      {!category.relatable && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <ul
              className="overflow-y-auto"
              style={{
                maxHeight,
              }}
            >
              {!!inputValue &&
                items.map((category, i) => (
                  <li
                    key={category.id}
                    {...getItemProps({
                      item: category,
                      index: i,
                    })}
                    className={clsx(
                      'px-4 py-2 md:py-1',
                      highlightedIndex === i && 'bg-pastel-green-500',
                    )}
                  >
                    {category.full_name}
                  </li>
                ))}
            </ul>
          </>
        )}
      </ul>
    </div>
  );
};

export default CategorySelector;
